// Libs
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import InfiniteScroll from 'react-infinite-scroll-component';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { ConversationMessage } from '~/components/module/conversation';
// Others
import { IMessage } from '~/utils/interface/message';
import { DateFormatEnum, TimeFormatEnum } from '~/utils/enum/common';
import { DEFAULT_NUMBER_ZERO } from '~/utils/constants/common';
// Styles, images, icons
import styles from './ConversationMessageContent.module.scss';

type Props = {
  data: IMessage[];
  isLoadingMore?: boolean;
  isLoadingMessage?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  hasMoreMessages: boolean;
  handleScroll?: () => void;
  onLoadMoreMessage?: () => void;
};

const cx = classNames.bind(styles);

const ConversationMessageContent = (props: Props) => {
  //#region Destructuring Props
  const {
    data,
    isLoadingMessage,
    isLoadingMore,
    containerRef,
    handleScroll,
    hasMoreMessages,
    onLoadMoreMessage,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/conversation']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleLoadMoreMessage = () => {
    if (isLoadingMessage || isLoadingMore) return;

    onLoadMoreMessage && onLoadMoreMessage();
  };
  //#endregion Handle Function

  return (
    <Box
      id='conversationMessageContentComponent'
      ref={containerRef}
      className={cx('conversationMessageContent')}
      onScroll={handleScroll}
    >
      <InfiniteScroll
        dataLength={data.length}
        next={handleLoadMoreMessage}
        hasMore={hasMoreMessages}
        inverse={true}
        loader={
          <Stack
            className={cx({ loader: data?.length === DEFAULT_NUMBER_ZERO })}
            direction={'row'}
            alignItems={'center'}
            justifyContent={'center'}
            height={35}
          >
            <CircularProgress size={24} />
          </Stack>
        }
        scrollableTarget={'conversationMessageContentComponent'}
        className={cx('infiniteScroll')}
      >
        {Array.isArray(data) &&
          (() => {
            return data.map((message, index) => {
              const formattedTime: string = moment(message?.createdAt).calendar(null, {
                sameDay: t('conversation_date_time_format_same_day', {
                  time: TimeFormatEnum.HOUR_MINUTE_AM_PM,
                }),
                nextDay: t('conversation_date_time_format_next_day', {
                  time: TimeFormatEnum.HOUR_MINUTE_AM_PM,
                }),
                nextWeek: t('conversation_date_time_format_next_week', {
                  date: DateFormatEnum.DDDD,
                  time: TimeFormatEnum.HOUR_MINUTE_AM_PM,
                }),
                lastDay: t('conversation_date_time_format_last_day', {
                  time: TimeFormatEnum.HOUR_MINUTE_AM_PM,
                }),
                lastWeek: t('conversation_date_time_format_last_week', {
                  date: DateFormatEnum.DDDD,
                  time: TimeFormatEnum.HOUR_MINUTE_AM_PM,
                }),
                sameElse: t('conversation_date_time_format_same_else', {
                  date: DateFormatEnum.DD_MMM_YYYY,
                  time: TimeFormatEnum.HOUR_MINUTE_AM_PM,
                }),
              });

              return (
                <Box key={message.id}>
                  <Typography component={'p'} className={cx('dateTime')}>
                    {formattedTime}
                  </Typography>

                  <ConversationMessage data={message} />
                </Box>
              );
            });
          })()}
      </InfiniteScroll>
    </Box>
  );
};

export default ConversationMessageContent;
